import { getSessionProperties } from '@farmersdog/sessions';
import {
  redactEmailsFromPath,
  ENGAGEMENT_TRACKER_VERSION,
} from '@farmersdog/utils';

import analytics from './analytics';
import { getSegmentOpts } from './helpers/getSegmentOpts';
export interface PageProperties extends Record<string, unknown> {
  title?: string;
}

export const titleTemplate = '%s | The Farmer’s Dog';
export interface PageArgs<T> {
  path: string;
  baseUrl: string;
  properties?: T;
  callbackIfMethodIsUndefined?: () => void;
}

/**
 *
 * Calls the Segment `page` function, with session data automatically added, email redacted from paths, and safe fallback when Segment is not available
 *
 * https://segment.com/docs/connections/spec/page/
 *
 * @param path - The path component of the current URL. This usually comes from `document.location`
 * @param baseUrl - The protocol and domain name of the consuming app. This can come from `document.origin` or a manual configuration
 * @param properties - Optional properties to attach to the `page` call
 * @param callbackIfMethodIsUndefined - If the Segment SDK is unavailable, this callback will be invoked. Example use-case is for logging
 *
 * @returns void
 *
 */
function page<T extends PageProperties>({
  path,
  baseUrl,
  properties,
  callbackIfMethodIsUndefined,
}: PageArgs<T>) {
  const sessionProperties = getSessionProperties(ENGAGEMENT_TRACKER_VERSION);
  const cleanedPath = redactEmailsFromPath(path);

  const formattedPropertiesWithSessions = {
    ...properties,
    ...sessionProperties,
    title: properties?.title
      ? titleTemplate.replace('%s', String(properties.title))
      : undefined,
    path: cleanedPath,
    url: `${baseUrl}${cleanedPath}`,
  };

  const opts = getSegmentOpts({ eventType: 'page' });

  analytics(callbackIfMethodIsUndefined).page(
    cleanedPath,
    formattedPropertiesWithSessions,
    opts
  );
}

export default page;
